import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Shield,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Award,
  Users
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyScoreWidget = ({ companyId = null }) => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSafetyScore();
  }, [companyId]);

  const fetchSafetyScore = async () => {
    try {
      const url = companyId 
        ? `${API}/safety-score/company/${companyId}`
        : `${API}/safety-score`;
      const response = await axios.get(url, { withCredentials: true });
      setScore(response.data);
    } catch (error) {
      console.error("Error fetching safety score:", error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-emerald-600 bg-emerald-100';
      case 'B': return 'text-blue-600 bg-blue-100';
      case 'C': return 'text-amber-600 bg-amber-100';
      case 'D': return 'text-orange-600 bg-orange-100';
      default: return 'text-rose-600 bg-rose-100';
    }
  };

  const getScoreColor = (value) => {
    if (value >= 80) return 'bg-emerald-500';
    if (value >= 60) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  if (loading) {
    return (
      <Card className="col-span-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!score) {
    return null;
  }

  const components = [
    {
      name: 'Training Compliance',
      key: 'training_compliance',
      icon: CheckCircle2,
      color: 'text-emerald-600',
      detail: `${score.components.training_compliance.completed}/${score.components.training_compliance.total} completed`
    },
    {
      name: 'Inspection Pass Rate',
      key: 'inspection_pass_rate',
      icon: Shield,
      color: 'text-blue-600',
      detail: `${score.components.inspection_pass_rate.passed}/${score.components.inspection_pass_rate.total} passed`
    },
    {
      name: 'Incident Rate',
      key: 'incident_rate',
      icon: AlertTriangle,
      color: 'text-amber-600',
      detail: `${score.components.incident_rate.total_incidents} incidents (${score.components.incident_rate.high_severity} high severity)`
    },
    {
      name: 'Certificate Compliance',
      key: 'certificate_compliance',
      icon: Award,
      color: 'text-violet-600',
      detail: `${score.components.certificate_compliance.valid} valid, ${score.components.certificate_compliance.expiring_soon} expiring`
    }
  ];

  return (
    <Card className="col-span-full overflow-hidden" data-testid="safety-score-widget">
      <CardContent className="p-0">
        <div className="grid lg:grid-cols-12">
          {/* Main Score */}
          <div className="lg:col-span-4 bg-gradient-to-br from-slate-800 to-slate-900 p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Safety Score</h3>
                <p className="text-slate-400 text-sm">{score.status}</p>
              </div>
            </div>
            
            <div className="flex items-end gap-4 mb-6">
              <span className="font-heading text-6xl font-bold">{score.overall_score}</span>
              <div className={`px-4 py-2 rounded-full font-bold text-xl ${getGradeColor(score.grade)}`}>
                {score.grade}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Users className="w-4 h-4" />
              <span>{score.employee_count} employees tracked</span>
            </div>
          </div>

          {/* Component Scores */}
          <div className="lg:col-span-8 p-6">
            <h4 className="font-heading font-semibold text-primary mb-4">Score Components</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              {components.map((comp) => {
                const Icon = comp.icon;
                const componentScore = score.components[comp.key]?.score || 0;
                const weight = score.components[comp.key]?.weight || 0;
                
                return (
                  <div key={comp.key} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${comp.color}`} />
                        <span className="font-medium text-sm text-primary">{comp.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{weight}% weight</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${getScoreColor(componentScore)}`}
                            style={{ width: `${componentScore}%` }}
                          />
                        </div>
                      </div>
                      <span className="font-semibold text-primary w-12 text-right">{componentScore}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{comp.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SafetyScoreWidget;
